import React, { useState, useCallback } from 'react';
import { RegistrationFormData } from '../types/registration';
import { t } from '../services/translation';

interface Step1Props {
  data: Pick<RegistrationFormData, 'email' | 'password' | 'passwordConfirm' | 'privacyAccepted' | 'recaptchaToken' | 'website'>;
  onChange: (fields: Partial<RegistrationFormData>) => void;
  onNext: () => void;
  globalError?: string;
  siteKey?: string;
}

const EyeIcon = ({ open }: { open: boolean }) => open ? (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const Step1LoginData: React.FC<Step1Props> = ({ data, onChange, onNext, globalError, siteKey }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validate = useCallback((): boolean => {
    const e: Record<string, string> = {};

    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      e.email = t('Érvényes e-mail cím megadása kötelező.');
    }

    const pwPattern = /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d\W]{8,}$/;
    if (!data.password) {
      e.password = t('Jelszó megadása kötelező.');
    } else if (!pwPattern.test(data.password)) {
      e.password = t('Min. 8 karakter, tartalmazzon nagybetűt és számot.');
    }

    if (!data.passwordConfirm) {
      e.passwordConfirm = t('Jelszó megerősítése kötelező.');
    } else if (data.password !== data.passwordConfirm) {
      e.passwordConfirm = t('A két jelszó nem egyezik.');
    }

    if (!data.privacyAccepted) {
      e.privacy = t('Az Adatvédelmi tájékoztató elfogadása kötelező.');
    }

    if (siteKey && !data.recaptchaToken) {
      e.recaptcha = t('Kérjük, töltsd ki a reCAPTCHA ellenőrzést.');
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [data, siteKey]);

  const handleNext = () => {
    setTouched({ email: true, password: true, passwordConfirm: true, privacy: true, recaptcha: true });
    if (validate()) onNext();
  };

  const handleBlur = (field: string) => {
    setTouched(t => ({ ...t, [field]: true }));
  };

  // reCAPTCHA callback
  React.useEffect(() => {
    if (!siteKey) return;
    (window as any).jwRecaptchaCallback = (token: string) => {
      onChange({ recaptchaToken: token });
      setErrors(prev => {
        const next = { ...prev };
        delete next.recaptcha;
        return next;
      });
    };
    (window as any).jwRecaptchaExpired = () => {
      onChange({ recaptchaToken: '' });
    };
  }, [siteKey, onChange]);

  return (
    <div className="jw-step-enter" id="jw-reg-step-1">
      <h2 className="jw-step-heading">{t('Belépési adatok')}</h2>

      <p className="jw-info-text">
        {t('A részvétel INGYENES, regisztrálj, és jelentkezz aktuális rendezvényünkre!')}
      </p>

      {globalError && (
        <div className="jw-global-error" role="alert">
          <span className="jw-global-error-icon">⚠</span>
          <span dangerouslySetInnerHTML={{ __html: globalError }} />
        </div>
      )}

      {/* EMAIL */}
      <div className="jw-field">
        <label className="jw-label" htmlFor="reg-email">
          {t('E-mail')} <span className="required">*</span>
        </label>
        <input
          id="reg-email"
          type="email"
          className={`jw-input${touched.email && errors.email ? ' error' : ''}`}
          value={data.email}
          autoComplete="email"
          onChange={e => onChange({ email: e.target.value })}
          onBlur={() => handleBlur('email')}
          placeholder={t('pelda@email.hu')}
        />
        {touched.email && errors.email && (
          <div className="jw-input-error">⚠ {errors.email}</div>
        )}
      </div>

      {/* JELSZÓ */}
      <div className="jw-field">
        <label className="jw-label" htmlFor="reg-pw">
          {t('Új jelszó')} <span className="required">*</span>
        </label>
        <div className="jw-password-wrap">
          <input
            id="reg-pw"
            type={showPw ? 'text' : 'password'}
            className={`jw-input${touched.password && errors.password ? ' error' : ''}`}
            value={data.password}
            autoComplete="new-password"
            onChange={e => onChange({ password: e.target.value })}
            onBlur={() => handleBlur('password')}
            placeholder={t('Min. 8 kar., nagybetű és szám')}
          />
          <button
            type="button"
            className="jw-password-toggle"
            onClick={() => setShowPw(v => !v)}
            aria-label={showPw ? t('Jelszó elrejtése') : t('Jelszó megjelenítése')}
          >
            <EyeIcon open={showPw} />
          </button>
        </div>
        {touched.password && errors.password && (
          <div className="jw-input-error">⚠ {errors.password}</div>
        )}
      </div>

      {/* JELSZÓ MEGERŐSÍTÉS */}
      <div className="jw-field">
        <label className="jw-label" htmlFor="reg-pw2">
          {t('Jelszó újra')} <span className="required">*</span>
        </label>
        <div className="jw-password-wrap">
          <input
            id="reg-pw2"
            type={showPw2 ? 'text' : 'password'}
            className={`jw-input${touched.passwordConfirm && errors.passwordConfirm ? ' error' : ''}`}
            value={data.passwordConfirm}
            autoComplete="new-password"
            onChange={e => onChange({ passwordConfirm: e.target.value })}
            onBlur={() => handleBlur('passwordConfirm')}
            placeholder={t('Jelszó ismétlése')}
          />
          <button
            type="button"
            className="jw-password-toggle"
            onClick={() => setShowPw2(v => !v)}
            aria-label={showPw2 ? t('Jelszó elrejtése') : t('Jelszó megjelenítése')}
          >
            <EyeIcon open={showPw2} />
          </button>
        </div>
        {touched.passwordConfirm && errors.passwordConfirm && (
          <div className="jw-input-error">⚠ {errors.passwordConfirm}</div>
        )}
      </div>

      {/* ADATVÉDELEM */}
      <div className="jw-field">
        <label className={`jw-privacy-wrap${data.privacyAccepted ? ' checked' : ''}`}>
          <input
            type="checkbox"
            checked={data.privacyAccepted}
            onChange={e => {
              onChange({ privacyAccepted: e.target.checked });
              handleBlur('privacy');
            }}
            id="reg-privacy"
          />
          <span className="jw-privacy-text">
            {t('Kijelentem, hogy az ')}
            <a href="/adatvedelmi-tajekoztato/" target="_blank" rel="noopener noreferrer">
              {t('Adatvédelmi tájékoztató')}
            </a>
            {t('t elolvastam, megértettem, elfogadom, és hozzájárulok, hogy az állásértesítőm létrehozásához Szervező az általam megadott adatokat felhasználja.')}
          </span>
        </label>
        {touched.privacy && errors.privacy && (
          <div className="jw-input-error">⚠ {errors.privacy}</div>
        )}
      </div>

      {/* Honeypot spamszűréshez: a felhasználók számára rejtett, de a spambotok kitöltik */}
      <div
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        <label htmlFor="reg-website">Website</label>
        <input
          type="text"
          id="reg-website"
          name="website"
          tabIndex={-1}
          autoComplete="new-password"
          value={data.website || ''}
          onChange={(e) => onChange({ website: e.target.value })}
        />
      </div>

      {/* RECAPTCHA */}
      {siteKey && (
        <div className="jw-recaptcha-wrap">
          <div
            className="g-recaptcha"
            data-sitekey={siteKey}
            data-callback="jwRecaptchaCallback"
            data-expired-callback="jwRecaptchaExpired"
          />
          {touched.recaptcha && errors.recaptcha && (
            <div className="jw-input-error">⚠ {errors.recaptcha}</div>
          )}
        </div>
      )}

      <div className="jw-required-info">{t('A kötelező mezőket * karakter jelöli.')}</div>

      <div className="jw-btn-row">
        <button
          type="button"
          className="jw-btn jw-btn-primary"
          onClick={handleNext}
          id="reg-step1-next"
        >
          {t('Tovább')} →
        </button>
      </div>
    </div>
  );
};

export default Step1LoginData;
